import { useEffect, useState, type ReactNode } from "react";
import {
  MAX_QUEUE_TURNS,
  MAX_QUEUED_MOVES_PER_TURN,
  defaultGameConfig,
  type GameConfig,
} from "@fortress/shared";
import {
  RESOURCE_KINDS,
  RESOURCE_LABELS,
  ResourceIcon,
  type ResourceKind,
} from "./resources";
import { buildingTokenSpec, shade } from "./buildingShapes";
import {
  BRIDGE_FILL_COLOR,
  BRIDGE_OUTLINE_COLOR,
  I_BEAM,
  iBeamSegments,
} from "./bridgeIBeam";
import { httpBase } from "./net/origin";

// Outside a room the rulebook fetches the server's DEFAULT rule snapshot (the
// config new matches are created with) so its numbers track the server, not
// this bundle. Fetched once and memoised; a failed fetch is not cached.
let defaultConfigPromise: Promise<GameConfig> | null = null;
function fetchDefaultConfig(): Promise<GameConfig> {
  if (!defaultConfigPromise) {
    defaultConfigPromise = fetch(`${httpBase()}/game-config`).then(
      async (res) => {
        if (!res.ok) throw new Error(`game-config HTTP ${res.status}`);
        return ((await res.json()) as { config: GameConfig }).config;
      },
    );
    defaultConfigPromise.catch(() => {
      defaultConfigPromise = null;
    });
  }
  return defaultConfigPromise;
}

/** Bundled code defaults — shown only until the server fetch lands (or if it
 * never does); built lazily so opening the app doesn't pay for the clone. */
let bundled: GameConfig | null = null;
const bundledConfig = () => (bundled ??= defaultGameConfig());

/** The example player colour the manual's building tokens are painted in (the
 * seat-2 blue from the board palette; in-game each player has their own). */
const TOKEN_COLOR = 0x5db9ff;

const cssColor = (c: number) => `#${c.toString(16).padStart(6, "0")}`;

/** A building token exactly as it appears on the board — SVG painted from the
 * SAME spec (buildingShapes.ts) the Pixi board renders from, at the board's
 * token radius, so the manual's images can never drift from the real look. */
function BuildingToken({
  kind,
  size = 30,
  color = TOKEN_COLOR,
}: {
  kind: "scout" | "barracks" | "mine" | "house";
  size?: number;
  color?: number;
}) {
  const spec = buildingTokenSpec(kind, 7.65); // the board's BUILD_R
  return (
    <svg width={size} height={size} viewBox="-12 -12 24 24" aria-hidden>
      {spec.layers.map((l, i) =>
        l.shape === "circle" ? (
          <circle
            key={i}
            cx={l.cx}
            cy={l.cy}
            r={l.r}
            fill={cssColor(shade(color, l.factor))}
            fillOpacity={l.alpha ?? 1}
          />
        ) : (
          <polygon
            key={i}
            points={l.points.map((p) => p.join(",")).join(" ")}
            fill={cssColor(shade(color, l.factor))}
            fillOpacity={l.alpha ?? 1}
          />
        ),
      )}
      {spec.rim.shape === "circle" ? (
        <circle
          cx={spec.rim.cx}
          cy={spec.rim.cy}
          r={spec.rim.r}
          fill="none"
          stroke="#fff"
          strokeWidth={1.8}
        />
      ) : (
        <polygon
          points={spec.rim.points.map((p) => p.join(",")).join(" ")}
          fill="none"
          stroke="#fff"
          strokeWidth={1.8}
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

/** The raised player-coloured X marking an empty node claimed by a fortress —
 * mirrors the board's drawFortressMarker (shadow, white keyline, coloured X). */
function FortressMark({ size = 30, color = TOKEN_COLOR }: { size?: number; color?: number }) {
  const d = 4.8;
  const arms = (x = 0, y = 0) =>
    `M${-d + x} ${-d + y} L${d + x} ${d + y} M${d + x} ${-d + y} L${-d + x} ${d + y}`;
  return (
    <svg width={size} height={size} viewBox="-12 -12 24 24" aria-hidden>
      <path
        d={arms(0.8, 1.05)}
        stroke={cssColor(shade(color, 0.42))}
        strokeOpacity={0.72}
        strokeWidth={5.2}
        strokeLinecap="round"
        fill="none"
      />
      <path d={arms()} stroke="#fff" strokeWidth={4.5} strokeLinecap="round" fill="none" />
      <path
        d={arms(-0.15, -0.2)}
        stroke={cssColor(color)}
        strokeWidth={2.5}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** A bridge as drawn on a path: the grey I-beam (shared bridgeIBeam geometry). */
function BridgeMark({ size = 30 }: { size?: number }) {
  const seg = iBeamSegments({ x: -11, y: 0 }, { x: 11, y: 0 });
  const path =
    `M${seg.web[0].x} ${seg.web[0].y} L${seg.web[1].x} ${seg.web[1].y} ` +
    seg.flanges.map(([p, q]) => `M${p.x} ${p.y} L${q.x} ${q.y}`).join(" ");
  return (
    <svg width={size} height={size} viewBox="-12 -12 24 24" aria-hidden>
      <path
        d={path}
        stroke={BRIDGE_OUTLINE_COLOR}
        strokeWidth={I_BEAM.outlineWidth * seg.len}
        strokeLinecap="round"
        fill="none"
      />
      <path
        d={path}
        stroke={BRIDGE_FILL_COLOR}
        strokeWidth={I_BEAM.fillWidth * seg.len}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** A chasm (severed path): the printed path is missing and a small "T" barrier
 * caps each bank — mirrors the board's severed-edge rendering. */
function ChasmMark({ size = 30 }: { size?: number }) {
  // pushBarrier geometry at board scale: stem 3.7→9.7 from each node, half 2.7.
  const bank = (nx: number, dir: number) =>
    `M${nx + dir * 3.7} 0 L${nx + dir * 9.7} 0 ` +
    `M${nx + dir * 9.7} -2.7 L${nx + dir * 9.7} 2.7`;
  return (
    <svg width={size} height={size} viewBox="-12 -12 24 24" aria-hidden>
      <circle cx={-12} cy={0} r={2.2} fill="#3a342c" />
      <circle cx={12} cy={0} r={2.2} fill="#3a342c" />
      <path
        d={`${bank(-12, 1)} ${bank(12, -1)}`}
        stroke="#2b2620"
        strokeWidth={1.2}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** One legend row: a parchment chip (so the mark reads exactly as on the board)
 * followed by its name and a one-line meaning. */
function LegendItem({
  chip,
  name,
  children,
  wide = false,
}: {
  chip: ReactNode;
  name: string;
  children: ReactNode;
  /** Widens the chip to fit two marks side by side (permanent + one-time). */
  wide?: boolean;
}) {
  return (
    <div className="legend-item">
      <span className={`legend-chip${wide ? " legend-chip-wide" : ""}`}>
        {chip}
      </span>
      <span>
        <strong>{name}</strong> — {children}
      </span>
    </div>
  );
}

/** One-line legend blurb per resource. */
const RESOURCE_BLURBS: Record<ResourceKind, string> = {
  minerals: "blue gem. The basic building material.",
  food: "green disc. Feeds attacking Barracks.",
  vigor: "red heart. Pays altitude penalties (some cards call it energy).",
  obsidian: "dark diamond. Buys fortress cards and bridges.",
};

/** Inline resource glyph + name, so costs read the same as the sidebar. */
function Res({ kind }: { kind: ResourceKind }) {
  return (
    <>
      <ResourceIcon kind={kind} size={13} /> {RESOURCE_LABELS[kind]}
    </>
  );
}

/** A cost tuple as "n <icon>" pairs (canonical resource order, zeros elided);
 * `labels` adds the resource name for use in prose. */
function Cost({
  amount,
  labels = false,
}: {
  amount: readonly number[];
  labels?: boolean;
}) {
  const parts = RESOURCE_KINDS.map((kind, i) => {
    const n = amount[i] ?? 0;
    if (n <= 0) return null;
    return (
      <span key={kind} className="rulebook-cost">
        {n} {labels ? <Res kind={kind} /> : <ResourceIcon kind={kind} size={13} />}
      </span>
    );
  }).filter(Boolean);
  return parts.length > 0 ? <>{parts}</> : <>—</>;
}

/** A zone's per-resource bonus tuple: "+n" when uniform (the usual case),
 * otherwise each non-zero resource spelled out. */
function Bonus({ bonus }: { bonus: readonly number[] }) {
  const first = bonus[0] ?? 0;
  if (bonus.every((n) => n === first)) {
    return <>{first > 0 ? `+${first}` : "—"}</>;
  }
  return (
    <>
      {RESOURCE_KINDS.map((kind, i) => {
        const n = bonus[i] ?? 0;
        if (n <= 0) return null;
        return (
          <span key={kind} className="rulebook-cost">
            +{n} <ResourceIcon kind={kind} size={13} />
          </span>
        );
      })}
    </>
  );
}

/** "build n · attack n · defend n" from a building's range config. */
function rangeLabel(range: {
  build: number;
  attack?: number;
  defend?: number;
}): string {
  const parts = [`build ${range.build}`];
  if (range.attack !== undefined) parts.push(`attack ${range.attack}`);
  if (range.defend !== undefined) parts.push(`defend ${range.defend}`);
  return parts.join(" · ");
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The in-app rulebook, opened from the "?" button on the hub, in the lobby
 * and in-game. All rule numbers come from `config` — the room's pinned match
 * snapshot when supplied, else the server's default config — so the text can
 * never drift from the rules actually in force. */
export function Rulebook({
  onClose,
  config = null,
}: {
  onClose: () => void;
  config?: GameConfig | null;
}) {
  // Escape closes it (mirrors the fortress library modal).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const [fetched, setFetched] = useState<GameConfig | null>(null);
  useEffect(() => {
    if (config) return;
    let cancelled = false;
    fetchDefaultConfig().then(
      (c) => {
        if (!cancelled) setFetched(c);
      },
      () => {
        // Unreachable server — the bundled defaults stay up.
      },
    );
    return () => {
      cancelled = true;
    };
  }, [config]);

  const cfg = config ?? fetched ?? bundledConfig();
  const zones = Object.entries(cfg.vigorZones)
    .map(([level, zone]) => [Number(level), zone] as const)
    .sort(([a], [b]) => a - b);
  const { scout, barracks, mine, bridge } = cfg.buildings;

  return (
    <div
      className="rulebook-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Fortress rulebook"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="rulebook-panel">
        <div className="rulebook-head">
          <h1>How to play Fortress</h1>
          <button
            type="button"
            className="rulebook-close"
            onClick={onClose}
            aria-label="Close rulebook"
          >
            ✕
          </button>
        </div>
        <div className="rulebook-body">
          <h2>Legend</h2>
          <h3>Buildings &amp; board marks</h3>
          <div className="rulebook-legend">
            <LegendItem chip={<BuildingToken kind="scout" />} name="Scout">
              round token. Cheap, far build reach.
            </LegendItem>
            <LegendItem chip={<BuildingToken kind="barracks" />} name="Barracks">
              triangle token. Attacks and defends.
            </LegendItem>
            <LegendItem chip={<BuildingToken kind="mine" />} name="Mine">
              square token. Harvests permanent resources.
            </LegendItem>
            <LegendItem chip={<BuildingToken kind="house" />} name="Home base">
              house token. Your free starting Mine.
            </LegendItem>
            <LegendItem chip={<FortressMark />} name="Fortress node">
              a node claimed by an implemented fortress card.
            </LegendItem>
            <LegendItem chip={<BridgeMark />} name="Bridge">
              spans a chasm so the path works again.
            </LegendItem>
            <LegendItem chip={<ChasmMark />} name="Chasm">
              a broken path (barred ends) — impassable until bridged.
            </LegendItem>
          </div>
          <h3>Resources</h3>
          <div className="rulebook-legend">
            {RESOURCE_KINDS.map((kind) => (
              <LegendItem
                key={kind}
                wide
                chip={
                  <>
                    <ResourceIcon kind={kind} size={22} crop={0} />
                    <ResourceIcon kind={kind} size={22} oneTime crop={0} />
                  </>
                }
                name={RESOURCE_LABELS[kind]}
              >
                {RESOURCE_BLURBS[kind]}
              </LegendItem>
            ))}
            <div className="legend-sep" />
            <LegendItem
              chip={<ResourceIcon kind="minerals" size={22} crop={0} />}
              name="Permanent resource"
            >
              solid icon. Stays on its node forever; only a <strong>Mine</strong>{" "}
              built there yields it (every round).
            </LegendItem>
            <LegendItem
              chip={<ResourceIcon kind="minerals" size={22} oneTime crop={0} />}
              name="One-time resource"
            >
              faded, dashed icon. Collected once by <em>any</em> building placed
              there, then gone.
            </LegendItem>
          </div>

          <h2>The goal</h2>
          <p>
            Climb the mountain and build <strong>Fortresses</strong>. Cards
            from the <strong>win market</strong> carry victory conditions —
            implement one and satisfy its condition and the game ends with your
            win. Cards from the <strong>regular market</strong> don&apos;t win
            on their own, but their effects power your climb.
          </p>

          <h2>The board</h2>
          <p>
            The mountain is one giant hexagon of tiles. You never occupy the
            hexes themselves — everything is built on the <strong>nodes</strong>{" "}
            (the corners of the hexes), and reach is traced along the{" "}
            <strong>paths</strong> that connect them. Some printed paths are
            broken by chasms (drawn dashed): nothing can be built or traced{" "}
            <em>across</em> a broken path until a Bridge spans it.
          </p>
          <p>
            The mountain rises through {zones.length}{" "}
            <strong>altitude zones</strong>. Higher zones are richer — resource
            nodes there pay a bonus — but harsher: building there costs extra{" "}
            <Res kind="vigor" />, and keeping an attack up there costs extra{" "}
            <Res kind="food" />.
          </p>
          <table>
            <thead>
              <tr>
                <th>Zone</th>
                <th>Resource bonus</th>
                <th>Vigor cost to build</th>
                <th>Attack upkeep</th>
              </tr>
            </thead>
            <tbody>
              {zones.map(([level, zone]) => (
                <tr key={level}>
                  <td>
                    {capitalize(zone.name)}
                    {level === 0 ? " (rim)" : ""}
                  </td>
                  <td>
                    <Bonus bonus={zone.oneTimeResourceBonus} />
                  </td>
                  <td>
                    {zone.vigorPenalty > 0 ? `${zone.vigorPenalty} Vigor` : "—"}
                  </td>
                  <td>{zone.barracksAttackUpkeep} Food</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2>Resources</h2>
          <p>
            Everything is paid in four resources: <Res kind="minerals" />,{" "}
            <Res kind="food" />, <Res kind="vigor" /> (some card text calls it
            energy) and <Res kind="obsidian" />. At the start of every round,
            each player collects their <strong>production</strong>.
          </p>
          <p>
            Nodes may carry a printed resource, in one of two forms — the icon
            tells them apart:
          </p>
          <ul>
            <li>
              <ResourceIcon kind="minerals" size={16} />{" "}
              <strong>Permanent</strong> (solid icon) — the resource never
              leaves the node, but only a <strong>Mine</strong> taps it: build a
              Mine there and your production of that resource rises by 1 + the
              zone&apos;s bonus every round (lost again if the Mine falls). A
              Scout or Barracks placed on a permanent resource gains{" "}
              <strong>nothing</strong> from it.
            </li>
            <li>
              <ResourceIcon kind="minerals" size={16} oneTime />{" "}
              <strong>One-time</strong> (faded, dashed icon) — a single pickup:
              building <em>anything</em> on the node collects it once (1 + the
              zone&apos;s bonus), then it&apos;s gone and the node reverts to
              bare.
            </li>
          </ul>

          <h2>Setup</h2>
          <ul>
            <li>
              {cfg.minPlayers}–{cfg.maxPlayers} players ready up in the lobby;
              the board is assembled from random tiles and the game starts a
              few seconds after everyone is ready.
            </li>
            <li>
              In turn order, each player places their <strong>home base</strong>{" "}
              — a free Mine — on a highlighted node of the outer{" "}
              {zones[0] ? zones[0][1].name : "forest"} ring.
            </li>
            <li>
              Once <em>every</em> home base is placed, each player splits{" "}
              <strong>{cfg.startingProduction} production points</strong> across
              the four resources, and receives starting resources — later seats
              start with extra <Res kind="minerals" /> (and the last seat an
              extra <Res kind="obsidian" />) to offset acting later.
            </li>
          </ul>

          <h2>A round</h2>
          <p>
            Everyone collects production, then players take turns starting from
            the <strong>first-turn marker</strong> holder (who may first swap up
            to {cfg.fortressCards.roundStartCycleMaxPerRow} cards out of each
            market for free). On your turn you have{" "}
            <strong>{cfg.actionsPerTurn} actions</strong>:
          </p>
          <ul>
            <li>
              <strong>Construct</strong> — place a Scout, Barracks or Mine on an
              empty node within build range of your buildings. A Barracks may
              optionally be built <strong>already aimed</strong> — pick its
              attack/defend target during placement, all in this one action.
            </li>
            <li>
              <strong>Bridge</strong> — span a path. A chasm only needs one end
              of the gap in range; a bridge helps everyone, not just you.
            </li>
            <li>
              <strong>Implement a fortress</strong> — buy a face-up card whose
              pattern your buildings form on the board.
            </li>
            <li>
              <strong>Aim a Barracks</strong> — commit a Barracks to attack or
              defend (see Combat).
            </li>
            <li>
              <strong>Cycle a market</strong> — swap face-up cards out of one
              market row,{" "}
              <Cost amount={cfg.fortressCards.cycleCostPerRegularCard} labels />{" "}
              per card, the whole batch for one action.
            </li>
          </ul>
          <p>
            Built something you regret? <strong>Undo</strong> takes back your
            most recent <strong>Construct</strong> or <strong>Bridge</strong> —
            the board goes back to exactly how it was and you get the action
            point back. It is offered only while the turn is still yours, only
            for the very last thing you did, and only for those two actions:
            once you take another action, end your turn, or spend your last
            action point, the build stands.
          </p>
          <p>
            <strong>End turn</strong> keeps you in the round;{" "}
            <strong>pass</strong> bows out until the next one. The first player
            to pass takes the first-turn marker and leads the next round, which
            begins when everyone has passed. You can also plan ahead with{" "}
            <strong>+ Queue</strong>: up to {MAX_QUEUED_MOVES_PER_TURN} moves
            for each of your next {MAX_QUEUE_TURNS} turns — they auto-play on
            your turn. If you queued fewer actions than you have, the turn ends
            for you only when you are away from the table; while you are
            connected it stops with you instead, so you can spend what is left
            (or undo a queued build) rather than be flown past your own turn.
            Play also pauses if a queued move becomes illegal or needs live
            input; use <strong>Queue Pass</strong> to leave the round.
          </p>

          <h2>Buildings</h2>
          <p>
            A new building must be within <strong>build range</strong> of one
            of your existing buildings, traced along unbroken paths. Scouts and
            Mines are passable, but you can never trace through an
            opponent&apos;s Barracks or fortress. Builds above the foothills
            also pay the zone&apos;s <Res kind="vigor" /> penalty.
          </p>
          <table>
            <thead>
              <tr>
                <th>Building</th>
                <th>Cost</th>
                <th>Range</th>
                <th>Does</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <span className="rulebook-building">
                    <span className="legend-chip">
                      <BuildingToken kind="scout" size={24} />
                    </span>
                    Scout
                  </span>
                </td>
                <td><Cost amount={scout.cost} /></td>
                <td>{rangeLabel(scout.range)}</td>
                <td>Cheap long reach up the mountain.</td>
              </tr>
              <tr>
                <td>
                  <span className="rulebook-building">
                    <span className="legend-chip">
                      <BuildingToken kind="barracks" size={24} />
                    </span>
                    Barracks
                  </span>
                </td>
                <td><Cost amount={barracks.cost} /></td>
                <td>{rangeLabel(barracks.range)}</td>
                <td>
                  Your army. Blocks opponents from tracing range through it.
                </td>
              </tr>
              <tr>
                <td>
                  <span className="rulebook-building">
                    <span className="legend-chip">
                      <BuildingToken kind="mine" size={24} />
                    </span>
                    Mine
                  </span>
                </td>
                <td><Cost amount={mine.cost} /></td>
                <td>{rangeLabel(mine.range)}</td>
                <td>
                  The only building that harvests a permanent resource (+
                  {mine.permanentResourceCost ?? 0} of that resource to build
                  there), adding it to your production.
                </td>
              </tr>
              <tr>
                <td>
                  <span className="rulebook-building">
                    <span className="legend-chip">
                      <BridgeMark size={24} />
                    </span>
                    Bridge
                  </span>
                </td>
                <td><Cost amount={bridge.cost} /></td>
                <td>{rangeLabel(bridge.range)}</td>
                <td>
                  Reconnects a chasm path. The only build with no vigor
                  penalty.
                </td>
              </tr>
            </tbody>
          </table>

          <h2>Fortress cards</h2>
          <p>
            A fortress card is a spatial <strong>pattern</strong> of buildings
            — circle = Scout, triangle = Barracks, square = Mine, star = any
            building, thick edges = required bridges. When your free buildings
            form the pattern anywhere on the board (any rotation or mirror),
            implement it by paying the card&apos;s <Res kind="obsidian" /> cost.
            The pattern hardens into a fortress you own; its X-marker nodes are
            claimed too, demolishing any opponent building sitting there — a{' '}
            <strong>protected</strong> building is the exception: it can never
            be demolished, so the fortress simply can&apos;t be placed over it.
          </p>
          <ul>
            <li>
              Fortress nodes are <strong>indestructible</strong> — attacks
              can&apos;t touch them (a few special cards are exceptions).
            </li>
            <li>
              A Barracks absorbed into a fortress may still defend, but never
              attack.
            </li>
            <li>
              Regular fortresses grant ongoing effects and special actions
              (shown in your effects tray); win fortresses end the game per the
              timing printed on the card.
            </li>
          </ul>

          <h2>Combat</h2>
          <p>
            Spend an action to <strong>aim</strong> an upright Barracks:{" "}
            <strong>attack</strong> an opponent&apos;s building within range{" "}
            {barracks.range.attack}, or <strong>defend</strong> any building
            within range {barracks.range.defend}. An aim is committed until the
            round ends, when every Barracks stands upright again. A freshly
            constructed Barracks can also be aimed as part of its build action
            (no separate Aim action needed).
          </p>
          <ul>
            <li>
              At the <strong>end of every turn</strong>, each attacked building
              falls if attackers <strong>outnumber</strong> defenders — ties
              survive. An upright Barracks defends itself with 1.
            </li>
            <li>
              Before the attack lands, threatened owners get a{" "}
              <strong>defense window</strong>: aim upright Barracks at their own
              attacked buildings for free, then confirm. That includes the
              player whose turn is ending — building onto a node an opponent
              already aims at never costs you the building without a chance to
              answer.
            </li>
            <li>
              <strong>Bridges</strong> can be attacked and defended exactly like
              buildings — but they belong to no one, so <em>anyone</em> may
              defend one (including reactively in the defense window), and
              destroying one steals nothing. A destroyed span over a chasm
              severs the path again. Bridges inside an implemented Fortress
              pattern can&apos;t be targeted.
            </li>
            {(barracks.stolenResourceCount ?? 0) > 0 && (
              <li>
                Destroying another player&apos;s building lets each attacker{" "}
                <strong>steal {barracks.stolenResourceCount}</strong> of the
                victim&apos;s resources — attacker&apos;s choice (card effects
                can hand the choice to the victim).
              </li>
            )}
            <li>
              After combat, each surviving Barracks that attacked must be fed
              its zone&apos;s <Res kind="food" /> upkeep — or it starves and is
              razed. Each aim is charged once; holding it through later turns
              costs nothing more.
            </li>
            <li>
              A <strong>protected</strong> building (shielded by a card effect)
              is indestructible for the <em>rest of the game</em> — attacks
              can&apos;t raze it, a rising Fortress can&apos;t demolish or
              conscript it, and no one can capture it. Only its owner can undo
              it, by recycling the building. Your own Fortresses&apos; X markers
              can be protected the same way.
            </li>
          </ul>

          <h2>Winning</h2>
          <p>
            Win-market cards check their condition immediately, at the start of
            your turn, or at the end of the round, as printed. The moment one is
            satisfied the game ends — the board stays open afterwards so
            everyone can study the final position. Spectators can watch any game
            live without taking a seat.
          </p>
        </div>
      </div>
    </div>
  );
}
